import React from 'react';
import {
  Box,
  InputBaseComponentProps,
  Stack,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';

/**
 * Props for the ControlledTextField component.
 * Extends MUI's TextFieldProps, omitting 'value' and 'onChange'.
 */
interface ControlledTextFieldProps<T> extends Omit<
  TextFieldProps,
  'value' | 'onChange'
> {
  /** The currently entered value (any type, converted to string) */
  inputValue: T;
  /** Function to update the value */
  setInputValue: React.Dispatch<React.SetStateAction<T>>;
  /** Maximum allowed characters (optional) */
  maxLength?: number;
  /** Whether to show text length counter */
  showTextLength?: boolean;
  isDataTable?: boolean;
  /** Optional custom parser from string to T */
  parse?: (value: string) => T;
  /** Optional formatter from T to string */
  format?: (value: T) => string;

  InputProps?: TextFieldProps['InputProps'] & {
    inputComponent?: React.ElementType<InputBaseComponentProps>;
  };
  min?: number;
}

/**
 * A controlled text field component using MUI's TextField.
 * This component provides a customizable and reusable input with controlled state.
 */
const ControlledTextField = <T,>({
  inputValue,
  setInputValue,
  maxLength,
  showTextLength = false,
  sx,
  isDataTable = true,
  parse = (v) => v as unknown as T,
  format = (v) => String(v),
  min,
  ...props
}: ControlledTextFieldProps<T>): React.ReactElement => {
  const stringValue = format(inputValue);

  return (
    <Box sx={{ width: '100%' }}>
      <TextField
        {...props}
        value={stringValue}
        onChange={(e) => {
          const value = e.target.value;
          if (!maxLength || value.length <= maxLength) {
            const parsedValue = parse(value);

            if (typeof min === 'number' && !isNaN(Number(parsedValue))) {
              const num = Number(parsedValue);
              if (num < min) {
                setInputValue(parse(String(min)));
                return;
              }
            }
            setInputValue(parsedValue);
          }
        }}
        inputProps={{ ...props?.inputProps, maxLength }}
        InputLabelProps={{ ...props?.InputLabelProps, shrink: true }}
        sx={{
          ...(isDataTable && {
            '& .MuiOutlinedInput-root': {
              '& > fieldset': {
                borderColor: '#EEE',
              },
            },
            width: '100%',
          }),
          ...sx,
        }}
        autoComplete='new-password'
      />
      {/* Character Counter */}
      {maxLength && showTextLength && (
        <Stack direction='row' justifyContent='flex-end' pr='14px' pt='3px'>
          <Typography
            sx={{ fontFamily: 'Roboto', fontSize: '12px', color: '#666' }}
          >
            {stringValue.length}/{maxLength}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

export default ControlledTextField;
